(() => {
  const configElement = document.getElementById('SiamayaCurrencyConfig');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  if (config.base !== 'THB') return;

  const currencies = ['THB', 'USD', 'EUR', 'JPY', 'CNY', 'CHF', 'AUD', 'HKD', 'SGD'];
  const preferenceKey = 'siamaya-display-currency-v1';
  const ratesKey = 'siamaya-display-rates-v1';
  const day = 86400000;
  const originals = new WeakMap();
  const formatterCache = new Map();
  let selected = 'THB';
  let rates = null;
  let rateRequest = null;
  let requestVersion = 0;
  let renderQueued = false;
  let status = '';

  const readStorage = (key) => {
    try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
  };
  const writeStorage = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { return; }
  };
  const validSnapshot = (snapshot) => {
    const now = Date.now();
    if (!snapshot || !Number.isFinite(snapshot.fetchedAt) || snapshot.fetchedAt > now || now - snapshot.fetchedAt >= day) return false;
    if (!Array.isArray(snapshot.rows) || snapshot.rows.length !== currencies.length - 1) return false;
    const quotes = new Set();
    return snapshot.rows.every((row) => {
      if (!row || typeof row !== 'object') return false;
      const date = typeof row.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(row.date) ? Date.parse(`${row.date}T00:00:00Z`) : NaN;
      if (row.base !== 'THB' || !currencies.slice(1).includes(row.quote) || quotes.has(row.quote)) return false;
      if (typeof row.rate !== 'number' || !Number.isFinite(row.rate) || row.rate <= 0) return false;
      if (!Number.isFinite(date) || date > now + day || now - date > 7 * day) return false;
      quotes.add(row.quote);
      return true;
    });
  };
  const getRates = async () => {
    if (validSnapshot(rates)) return rates;
    const cached = readStorage(ratesKey);
    if (validSnapshot(cached)) {
      rates = cached;
      return rates;
    }
    if (rateRequest) return rateRequest;
    rateRequest = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch('https://api.frankfurter.dev/v2/rates?base=THB&quotes=USD,EUR,JPY,CNY,CHF,AUD,HKD,SGD', {
          signal: controller.signal,
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
        });
        if (!response.ok) throw new Error('Currency rates unavailable');
        const snapshot = { fetchedAt: Date.now(), rows: await response.json() };
        if (!validSnapshot(snapshot)) throw new Error('Invalid currency rates');
        rates = snapshot;
        writeStorage(ratesKey, snapshot);
        return snapshot;
      } finally {
        clearTimeout(timeout);
      }
    })();
    try { return await rateRequest; } finally { rateRequest = null; }
  };
  const format = (cents, currency) => {
    if (!formatterCache.has(currency)) {
      formatterCache.set(currency, new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency, currencyDisplay: 'code',
      }));
    }
    const rate = rates.rows.find((row) => row.quote === currency).rate;
    return `≈ ${formatterCache.get(currency).format(cents / 100 * rate)}`;
  };
  const render = () => {
    renderQueued = false;
    document.querySelectorAll('[data-siamaya-currency-picker]').forEach((picker) => { picker.hidden = false; });
    document.querySelectorAll('[data-siamaya-currency-select]').forEach((select) => {
      if (select.value !== selected) select.value = selected;
      const wide = selected === 'CNY' ? 'true' : 'false';
      if (select.dataset.wide !== wide) select.dataset.wide = wide;
    });
    document.querySelectorAll('[data-siamaya-currency-status]').forEach((element) => {
      if (element.textContent !== status) element.textContent = status;
      element.hidden = !status;
    });
    document.querySelectorAll('[data-siamaya-currency-notice]').forEach((element) => { element.hidden = selected === 'THB'; });
    document.querySelectorAll('[data-siamaya-money]').forEach((element) => {
      const cents = Number(element.dataset.siamayaMoney);
      if (!element.dataset.siamayaMoney || !Number.isSafeInteger(cents)) return;
      let original = originals.get(element);
      if (!original || original.cents !== cents || element.innerHTML !== original.rendered) {
        original = { cents, html: element.innerHTML, rendered: element.innerHTML };
        originals.set(element, original);
      }
      if (selected === 'THB') {
        if (element.innerHTML !== original.html) element.innerHTML = original.html;
      } else {
        const text = format(cents, selected);
        if (element.textContent !== text) element.textContent = text;
      }
      original.rendered = element.innerHTML;
    });
  };
  const scheduleRender = () => {
    if (renderQueued) return;
    renderQueued = true;
    queueMicrotask(render);
  };
  const choose = async (currency) => {
    if (!currencies.includes(currency)) return;
    const version = ++requestVersion;
    status = '';
    if (currency === 'THB') {
      selected = 'THB';
      writeStorage(preferenceKey, selected);
      render();
      return;
    }
    status = config.loading;
    render();
    try {
      await getRates();
      if (version !== requestVersion) return;
      selected = currency;
      status = '';
    } catch {
      if (version !== requestVersion) return;
      selected = 'THB';
      status = config.unavailable;
    }
    writeStorage(preferenceKey, selected);
    render();
  };

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-siamaya-currency-select]')) choose(event.target.value);
  });
  const relevant = '[data-siamaya-money], [data-siamaya-currency-picker], [data-siamaya-currency-notice]';
  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => {
      if (mutation.type === 'attributes' || mutation.target.parentElement?.closest('[data-siamaya-money]') || mutation.target.matches?.('[data-siamaya-money]')) return true;
      return Array.from(mutation.addedNodes).some((node) => node.nodeType === 1 && (node.matches(relevant) || node.querySelector(relevant)));
    })) scheduleRender();
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-siamaya-money'] });
  document.addEventListener('shopify:section:load', scheduleRender);
  render();
  const saved = readStorage(preferenceKey);
  if (currencies.includes(saved) && saved !== 'THB') choose(saved);
})();
